import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { LiveOpsDemo } from "@/components/landing/LiveOpsDemo";
import { OrbitingCirclesGlobe } from "@/components/ui/orbiting-circles-globe";
import { GITHUB_OSS } from "@/lib/hosts";

function GitHubMark({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

const ROTATING = [
  "agent harness",
  "Claude session",
  "Grok run",
  "Cursor window",
  "Codex worker",
  "Hermes bot",
  "machine on the team",
];

/** Headline + Live Ops demo (center) + globe directly under the demo. */
export function Hero() {
  const [ri, setRi] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const id = window.setInterval(() => {
      setFade(false);
      window.setTimeout(() => {
        setRi((i) => (i + 1) % ROTATING.length);
        setFade(true);
      }, 220);
    }, 2800);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden pt-24 pb-8 md:pt-28 md:pb-12">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 70% 45% at 50% 0%, rgba(249,115,22,0.14), transparent 55%), radial-gradient(ellipse 50% 35% at 85% 30%, rgba(168,85,247,0.1), transparent 50%)",
        }}
      />

      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mx-auto mb-7 max-w-3xl text-center md:mb-9">
          {/* One visual line; only the tail phrase rotates */}
          <h1 className="whitespace-nowrap text-[clamp(1.15rem,4.2vw,3rem)] font-bold tracking-tight text-white leading-tight">
            One hub for every{" "}
            <span
              className={`og-text-gradient inline-block min-w-[10ch] text-left transition-opacity duration-200 ${
                fade ? "opacity-100" : "opacity-0"
              }`}
            >
              {ROTATING[ri]}
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-zinc-400 sm:text-base md:text-lg">
            Many agents across many machines — not one chat on one box.
          </p>
          <div className="mt-5 flex flex-col items-center justify-center gap-2.5 sm:flex-row">
            <a
              href={GITHUB_OSS}
              target="_blank"
              rel="noopener noreferrer"
              className="og-btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"
            >
              Get started
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={GITHUB_OSS}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm font-medium text-zinc-200 transition hover:border-white/30 hover:text-white"
            >
              <GitHubMark className="h-4 w-4 shrink-0" />
              github.com/mrdulasolutions/open-gateway
            </a>
          </div>
        </div>

        <LiveOpsDemo className="mx-auto w-full max-w-5xl" />
      </div>

      {/* Extra air under demo; larger bold white globe header */}
      <div className="relative mt-20 md:mt-28">
        <h2 className="px-4 text-center text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl">
          Works with every harness on your team
        </h2>
        <p className="mx-auto mt-4 max-w-lg px-4 text-center text-sm text-zinc-400 sm:text-base">
          Different tools. Different machines. One shared radio.
        </p>
        <div className="mt-6">
          <OrbitingCirclesGlobe />
        </div>
      </div>
    </section>
  );
}
